import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Shield,
  Users,
  FileText,
  FileCheck,
  TrendingUp,
  Search,
  RefreshCw,
  Eye,
  MoreHorizontal,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Filter,
  Download,
  Settings,
  Bell,
  LogOut,
  Home,
  Briefcase,
  CreditCard,
  AlertCircle,
  CheckCircle,
  Clock,
  Euro,
  Activity,
  Target,
  Award,
  Percent,
  ChevronRight,
  UserPlus,
  Mail,
  Phone,
  Building,
  Handshake,
  HeadphonesIcon,
  Bot,
  Flame,
  ThermometerSun,
  Snowflake,
  MessageSquare,
  Zap,
  BrainCircuit,
  Car,
  Bike,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { ChatSummaries } from "@/components/admin/ChatSummaries";
import { AdminGate } from "@/components/admin/AdminGate";
import { CalcLeads } from "@/components/admin/CalcLeads";
import { PricingEditor } from "@/components/admin/PricingEditor";
import { ReviewReplies } from "@/components/admin/ReviewReplies";
import { PlateUsageCard } from "@/components/admin/PlateUsageCard";
import { Progress } from "@/components/ui/progress";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo JYVIS
const JYVIS_LOGO = "/logo.svg";

// Logo TheGoodDeal.fr
const THEGOODDEAL_LOGO = "/images/thegooddeal.jpg";

const statusColors = {
  draft: "bg-slate-100 text-slate-700",
  pending: "bg-amber-100 text-amber-700",
  accepted: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
  expired: "bg-gray-100 text-gray-600",
  active: "bg-emerald-100 text-emerald-700",
  pending_signature: "bg-blue-100 text-blue-700",
  suspended: "bg-orange-100 text-orange-700",
  cancelled: "bg-red-100 text-red-700",
};

const statusLabels = {
  draft: "Brouillon",
  pending: "En attente",
  accepted: "Accepté",
  rejected: "Refusé",
  expired: "Expiré",
  active: "Actif",
  pending_signature: "Signature",
  suspended: "Suspendu",
  cancelled: "Annulé",
};

const insuranceLabels = {
  auto: "Auto",
  moto: "Moto",
  sante: "Santé",
  prevoyance: "Prévoyance",
  rc_decennale: "RC Décennale",
  multirisque: "Multirisque",
  cybersecurite: "Cybersécurité",
  pret_immobilier: "Prêt Immobilier",
  vie: "Vie",
  retraite: "Retraite",
};

// Navigation items
const navItems = [
  { id: "dashboard", icon: BarChart3, label: "Tableau de bord" },
  { id: "quotes", icon: FileText, label: "Devis" },
  { id: "contracts", icon: FileCheck, label: "Contrats" },
  { id: "temporaire", icon: Clock, label: "Temporaire" },
  { id: "clients", icon: Users, label: "Clients" },
  { id: "commissions", icon: Euro, label: "Commissions" },
  { id: "calc-leads", icon: FileText, label: "Leads calculateurs" },
  { id: "pricing", icon: Euro, label: "Grilles tarifaires" },
  { id: "reviews", icon: MessageSquare, label: "Avis Google" },
  { id: "ai-insights", icon: BrainCircuit, label: "IA & Leads" },
  { id: "partners", icon: Handshake, label: "Partenaires" },
  { id: "support", icon: HeadphonesIcon, label: "Support" },
];

export default function AdminDashboard() {
  return <AdminGate><AdminDashboardInner /></AdminGate>;
}

function AdminDashboardInner() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Filters
  const [quoteStatus, setQuoteStatus] = useState("");
  const [quoteType, setQuoteType] = useState("");
  const [contractStatus, setContractStatus] = useState("");
  const [contractType, setContractType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [financialData, setFinancialData] = useState(null);
  const [aiDashboard, setAiDashboard] = useState(null);
  const [temporaireStats, setTemporaireStats] = useState(null);

  const [recentActivities] = useState([
    { type: "quote", action: "Nouveau devis auto", client: "Jean Dupont", time: "Il y a 5 min" },
    { type: "contract", action: "Contrat signé", client: "Marie Martin", time: "Il y a 15 min" },
    { type: "payment", action: "Paiement reçu", client: "Pierre Bernard", time: "Il y a 1h" },
    { type: "support", action: "Ticket résolu", client: "Sophie Petit", time: "Il y a 2h" },
  ]);

  const [performanceData] = useState({
    conversion_rate: 68,
    avg_response_time: "2h 15min",
    client_satisfaction: 94,
    contracts_target: 75,
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, quotesRes, contractsRes, clientsRes, financialRes, aiRes, temporaireRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/quotes`),
        axios.get(`${API}/admin/contracts`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/admin/financial-summary`).catch(() => ({ data: null })),
        axios.get(`${API}/ai/admin/dashboard`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/temporaire-stats`).catch(() => ({ data: null })),
      ]);
      
      setStats(statsRes.data);
      setQuotes(quotesRes.data.quotes || []);
      setContracts(contractsRes.data.contracts || []);
      setClients(clientsRes.data || []);
      setFinancialData(financialRes.data);
      setAiDashboard(aiRes.data);
      setTemporaireStats(temporaireRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Erreur lors du chargement des données");
    }
    setLoading(false);
  };

  const fetchQuotes = async () => {
    try {
      const params = new URLSearchParams();
      if (quoteStatus && quoteStatus !== "all") params.append("status", quoteStatus);
      if (quoteType && quoteType !== "all") params.append("insurance_type", quoteType);
      
      const res = await axios.get(`${API}/admin/quotes?${params.toString()}`);
      setQuotes(res.data.quotes);
    } catch (err) {
      console.error("Error fetching quotes:", err);
    }
  };

  const fetchContracts = async () => {
    try {
      const params = new URLSearchParams();
      if (contractStatus && contractStatus !== "all") params.append("status", contractStatus);
      if (contractType && contractType !== "all") params.append("insurance_type", contractType);
      
      const res = await axios.get(`${API}/admin/contracts?${params.toString()}`);
      setContracts(res.data.contracts);
    } catch (err) {
      console.error("Error fetching contracts:", err);
    }
  };

  const confirmTemporaire = async (id) => {
    try {
      await axios.patch(`${API}/admin/temporaire/${id}/confirm`);
      toast.success("Paiement validé");
      fetchAllData();
    } catch (err) {
      toast.error("Erreur lors de la validation");
    }
  };

  const activateContract = async (contractId) => {
    try {
      await axios.post(`${API}/contracts/${contractId}/activate`);
      toast.success("Contrat activé avec succès");
      fetchAllData();
    } catch (err) {
      toast.error("Erreur lors de l'activation");
    }
  };

  useEffect(() => {
    if (!loading) {
      fetchQuotes();
    }
  }, [quoteStatus, quoteType]);

  useEffect(() => {
    if (!loading) {
      fetchContracts();
    }
  }, [contractStatus, contractType]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#102692]">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
          <p className="text-white/70">Chargement du CRM...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar - Blue */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#102692] transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-200 ease-in-out`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <Link to="/" className="flex items-center gap-3">
              {/* Logo animé avec bordure violette */}
              <motion.div className="relative">
                <motion.div
                  className="absolute -inset-1 rounded-xl"
                  style={{
                    background: "conic-gradient(from 0deg, #8B5CF6, #A855F7, #D946EF, #EC4899, #8B5CF6)",
                  }}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                />
                <div className="relative bg-white rounded-lg p-1">
                  <img src={JYVIS_LOGO} alt="JYVIS Assurances" className="h-8 w-auto" />
                </div>
              </motion.div>
              {/* Logo TheGoodDeal */}
              <a 
                href="https://thegooddeal.fr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity mt-2"
              >
                <img src={THEGOODDEAL_LOGO} alt="TheGoodDeal.fr" className="h-6 w-auto rounded" />
              </a>
            </Link>
            <p className="text-xs text-white/70 mt-2">Back-office CRM</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  activeTab === item.id
                    ? 'bg-primary text-white'
                    : 'text-white/60 hover:bg-white/5 hover:text-white'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            ))}
          </nav>

          {/* Bottom section */}
          <div className="p-4 border-t border-white/10 space-y-2">
            <button className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-white/60 hover:bg-white/5 hover:text-white transition-all">
              <Settings className="w-5 h-5" />
              Paramètres
            </button>
            <Link to="/">
              <button className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-white/60 hover:bg-white/5 hover:text-white transition-all">
                <Home className="w-5 h-5" />
                Voir le site
              </button>
            </Link>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 min-h-screen" data-testid="admin-dashboard">
        {/* Top Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button 
                className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
                onClick={() => setSidebarOpen(true)}
              >
                <BarChart3 className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-heading font-bold text-slate-900">
                  {navItems.find(i => i.id === activeTab)?.label || 'Tableau de bord'}
                </h1>
                <p className="text-sm text-slate-500">
                  {new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  placeholder="Rechercher..." 
                  className="pl-10 w-64 bg-slate-50"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-input"
                />
              </div>
              <Button variant="outline" size="icon" className="relative" data-testid="notifications-btn">
                <Bell className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-white text-xs rounded-full flex items-center justify-center">3</span>
              </Button>
              <Button variant="outline" onClick={fetchAllData} data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualiser
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <MoreHorizontal className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem>
                    <Download className="w-4 h-4 mr-2" />
                    Exporter les données
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Settings className="w-4 h-4 mr-2" />
                    Paramètres
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-6">
          {/* Dashboard Tab */}
          {activeTab === "dashboard" && (
            <div className="space-y-6">
              <PlateUsageCard />
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                  <Card className="border-0 shadow-sm" data-testid="stat-clients">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500 mb-1">Clients actifs</p>
                          <p className="text-3xl font-bold text-slate-900">{stats?.total_clients || 0}</p>
                          <div className="flex items-center gap-1 mt-2 text-emerald-600 text-sm">
                            <ArrowUpRight className="w-4 h-4" />
                            +12% ce mois
                          </div>
                        </div>
                        <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center">
                          <Users className="w-7 h-7 text-blue-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                  <Card className="border-0 shadow-sm" data-testid="stat-quotes">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500 mb-1">Devis en attente</p>
                          <p className="text-3xl font-bold text-slate-900">{stats?.pending_quotes || 0}</p>
                          <div className="flex items-center gap-1 mt-2 text-amber-600 text-sm">
                            <Clock className="w-4 h-4" />
                            À traiter
                          </div>
                        </div>
                        <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center">
                          <FileText className="w-7 h-7 text-amber-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                  <Card className="border-0 shadow-sm" data-testid="stat-contracts">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500 mb-1">Contrats actifs</p>
                          <p className="text-3xl font-bold text-slate-900">{stats?.active_contracts || 0}</p>
                          <div className="flex items-center gap-1 mt-2 text-emerald-600 text-sm">
                            <CheckCircle className="w-4 h-4" />
                            En cours
                          </div>
                        </div>
                        <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center">
                          <FileCheck className="w-7 h-7 text-emerald-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                  <Card className="border-0 shadow-sm" data-testid="stat-revenue">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500 mb-1">Chiffre d'affaires</p>
                          <p className="text-3xl font-bold text-slate-900">{(stats?.total_revenue || 0).toLocaleString()}€</p>
                          <div className="flex items-center gap-1 mt-2 text-emerald-600 text-sm">
                            <TrendingUp className="w-4 h-4" />
                            +8.5% ce mois
                          </div>
                        </div>
                        <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center">
                          <Euro className="w-7 h-7 text-primary" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>

              {/* Performance & Activity */}
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Performance Metrics */}
                <Card className="border-0 shadow-sm lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg">Performance commerciale</CardTitle>
                    <CardDescription>Indicateurs clés de performance</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">Taux de conversion</span>
                          <span className="font-semibold text-emerald-600">{performanceData.conversion_rate}%</span>
                        </div>
                        <Progress value={performanceData.conversion_rate} className="h-2" />
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">Objectif contrats</span>
                          <span className="font-semibold text-primary">{performanceData.contracts_target}%</span>
                        </div>
                        <Progress value={performanceData.contracts_target} className="h-2" />
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">Satisfaction client</span>
                          <span className="font-semibold text-emerald-600">{performanceData.client_satisfaction}%</span>
                        </div>
                        <Progress value={performanceData.client_satisfaction} className="h-2" />
                      </div>
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-sm text-slate-500 mb-1">Temps de réponse moyen</p>
                        <p className="text-xl font-bold text-slate-900">{performanceData.avg_response_time}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Activity */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Activité récente</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {recentActivities.map((activity, index) => (
                      <div key={index} className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          activity.type === 'quote' ? 'bg-blue-100' :
                          activity.type === 'contract' ? 'bg-emerald-100' :
                          activity.type === 'payment' ? 'bg-primary/10' : 'bg-purple-100'
                        }`}>
                          {activity.type === 'quote' && <FileText className="w-4 h-4 text-blue-600" />}
                          {activity.type === 'contract' && <FileCheck className="w-4 h-4 text-emerald-600" />}
                          {activity.type === 'payment' && <CreditCard className="w-4 h-4 text-primary" />}
                          {activity.type === 'support' && <HeadphonesIcon className="w-4 h-4 text-purple-600" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-900">{activity.action}</p>
                          <p className="text-xs text-slate-500">{activity.client} • {activity.time}</p>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions & Commission Overview */}
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Quick Actions */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Actions rapides</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3">
                      <Button variant="outline" className="h-auto py-4 flex flex-col gap-2" onClick={() => setActiveTab('quotes')}>
                        <FileText className="w-5 h-5 text-primary" />
                        <span className="text-sm">Nouveau devis</span>
                      </Button>
                      <Button variant="outline" className="h-auto py-4 flex flex-col gap-2" onClick={() => setActiveTab('clients')}>
                        <UserPlus className="w-5 h-5 text-primary" />
                        <span className="text-sm">Ajouter client</span>
                      </Button>
                      <Button variant="outline" className="h-auto py-4 flex flex-col gap-2">
                        <Mail className="w-5 h-5 text-primary" />
                        <span className="text-sm">Envoyer email</span>
                      </Button>
                      <Button variant="outline" className="h-auto py-4 flex flex-col gap-2">
                        <Download className="w-5 h-5 text-primary" />
                        <span className="text-sm">Exporter</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Commission Overview */}
                <Card className="border-0 shadow-sm bg-gradient-to-br from-[#102692] to-[#2563EB] text-white">
                  <CardHeader>
                    <CardTitle className="text-lg text-white">Aperçu des commissions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-white/5 rounded-xl">
                        <p className="text-sm text-white/60 mb-1">Total gagné</p>
                        <p className="text-2xl font-bold">{(stats?.total_commissions || 0).toLocaleString()}€</p>
                      </div>
                      <div className="p-4 bg-white/5 rounded-xl">
                        <p className="text-sm text-white/60 mb-1">En attente</p>
                        <p className="text-2xl font-bold text-amber-300">{(stats?.pending_commissions || 0).toLocaleString()}€</p>
                      </div>
                      <div className="p-4 bg-white/5 rounded-xl">
                        <p className="text-sm text-white/60 mb-1">Frais de dossier</p>
                        <p className="text-2xl font-bold">{(stats?.total_frais_dossier || 0).toLocaleString()}€</p>
                      </div>
                      <div className="p-4 bg-white/5 rounded-xl">
                        <p className="text-sm text-white/60 mb-1">Taux moyen</p>
                        <p className="text-2xl font-bold text-emerald-400">{stats?.business_settings?.commission_rate_default || 15}%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Quotes Tab */}
          {activeTab === "quotes" && (
            <div className="space-y-6">
              {/* Filters */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-4">
                    <Select value={quoteStatus} onValueChange={setQuoteStatus}>
                      <SelectTrigger className="w-[180px]" data-testid="quote-status-filter">
                        <SelectValue placeholder="Statut" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous les statuts</SelectItem>
                        <SelectItem value="pending">En attente</SelectItem>
                        <SelectItem value="accepted">Accepté</SelectItem>
                        <SelectItem value="rejected">Refusé</SelectItem>
                        <SelectItem value="expired">Expiré</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={quoteType} onValueChange={setQuoteType}>
                      <SelectTrigger className="w-[180px]" data-testid="quote-type-filter">
                        <SelectValue placeholder="Type d'assurance" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous les types</SelectItem>
                        {Object.entries(insuranceLabels).map(([key, label]) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="outline">
                      <Filter className="w-4 h-4 mr-2" />
                      Plus de filtres
                    </Button>
                    <div className="flex-1" />
                    <Button className="bg-primary hover:bg-primary/90">
                      <FileText className="w-4 h-4 mr-2" />
                      Nouveau devis
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Quotes Table */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Référence</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Montant</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {quotes.map((quote) => (
                        <TableRow key={quote.id} className="hover:bg-slate-50/50">
                          <TableCell className="font-medium">#{quote.quote_number}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{quote.client_info?.first_name} {quote.client_info?.last_name}</p>
                              <p className="text-sm text-slate-500">{quote.client_info?.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{insuranceLabels[quote.insurance_type] || quote.insurance_type}</Badge>
                          </TableCell>
                          <TableCell className="font-semibold">{quote.monthly_premium?.toFixed(2)}€/mois</TableCell>
                          <TableCell>
                            <Badge className={statusColors[quote.status]}>
                              {statusLabels[quote.status] || quote.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-500">
                            {new Date(quote.created_at).toLocaleDateString('fr-FR')}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem>
                                  <Eye className="w-4 h-4 mr-2" />
                                  Voir détails
                                </DropdownMenuItem>
                                <DropdownMenuItem>
                                  <FileCheck className="w-4 h-4 mr-2" />
                                  Convertir en contrat
                                </DropdownMenuItem>
                                <DropdownMenuItem>
                                  <Mail className="w-4 h-4 mr-2" />
                                  Relancer le client
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {quotes.length === 0 && (
                    <div className="py-12 text-center text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Aucun devis trouvé</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Contracts Tab */}
          {activeTab === "contracts" && (
            <div className="space-y-6">
              {/* Filters */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-4">
                    <Select value={contractStatus} onValueChange={setContractStatus}>
                      <SelectTrigger className="w-[180px]" data-testid="contract-status-filter">
                        <SelectValue placeholder="Statut" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous les statuts</SelectItem>
                        <SelectItem value="active">Actif</SelectItem>
                        <SelectItem value="pending_signature">En attente de signature</SelectItem>
                        <SelectItem value="suspended">Suspendu</SelectItem>
                        <SelectItem value="cancelled">Annulé</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={contractType} onValueChange={setContractType}>
                      <SelectTrigger className="w-[180px]" data-testid="contract-type-filter">
                        <SelectValue placeholder="Type d'assurance" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous les types</SelectItem>
                        {Object.entries(insuranceLabels).map(([key, label]) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Contracts Table */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>N° Contrat</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Prime</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Échéance</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contracts.map((contract) => (
                        <TableRow key={contract.id} className="hover:bg-slate-50/50">
                          <TableCell className="font-medium">{contract.reference}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{contract.client_info?.first_name} {contract.client_info?.last_name}</p>
                              <p className="text-sm text-slate-500">{contract.client_info?.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{insuranceLabels[contract.insurance_type] || contract.insurance_type}</Badge>
                          </TableCell>
                          <TableCell className="font-semibold">{contract.monthly_premium?.toFixed(2)}€/mois</TableCell>
                          <TableCell>
                            <Badge className={statusColors[contract.status]}>
                              {statusLabels[contract.status] || contract.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-500">
                            {contract.end_date ? new Date(contract.end_date).toLocaleDateString('fr-FR') : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem>
                                  <Eye className="w-4 h-4 mr-2" />
                                  Voir détails
                                </DropdownMenuItem>
                                {contract.status === 'pending_signature' && (
                                  <DropdownMenuItem onClick={() => activateContract(contract.id)}>
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Activer
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem>
                                  <Download className="w-4 h-4 mr-2" />
                                  Télécharger PDF
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {contracts.length === 0 && (
                    <div className="py-12 text-center text-slate-500">
                      <FileCheck className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Aucun contrat trouvé</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Temporaire Tab */}
          {activeTab === "temporaire" && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid md:grid-cols-4 gap-4">
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Clock className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{temporaireStats?.total || 0}</p>
                        <p className="text-xs text-slate-500">Total souscriptions</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{temporaireStats?.confirmed || 0}</p>
                        <p className="text-xs text-slate-500">Confirmés (payés)</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{temporaireStats?.pending || 0}</p>
                        <p className="text-xs text-slate-500">En attente paiement</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                        <Euro className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{temporaireStats?.total_revenue || 0} €</p>
                        <p className="text-xs text-slate-500">CA généré</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Répartition Auto/Moto */}
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Car className="w-5 h-5" />
                      Assurances Auto
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center">
                      <p className="text-5xl font-bold text-blue-600">{temporaireStats?.auto_count || 0}</p>
                      <p className="text-slate-500 mt-2">souscriptions auto temporaires</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Bike className="w-5 h-5" />
                      Assurances Moto
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center">
                      <p className="text-5xl font-bold text-orange-600">{temporaireStats?.moto_count || 0}</p>
                      <p className="text-slate-500 mt-2">souscriptions moto temporaires</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Subscriptions Table */}
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Dernières souscriptions temporaires</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Client</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Durée</TableHead>
                        <TableHead>Plaque</TableHead>
                        <TableHead>Prix</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {temporaireStats?.recent?.map((sub) => (
                        <TableRow key={sub.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{sub.prenom} {sub.nom}</p>
                              <p className="text-xs text-slate-500">{sub.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={sub.vehiculeType === "auto" ? "bg-blue-50" : "bg-orange-50"}>
                              {sub.vehiculeType === "auto" ? <Car className="w-3 h-3 mr-1" /> : <Bike className="w-3 h-3 mr-1" />}
                              {sub.vehiculeType === "auto" ? "Auto" : "Moto"}
                            </Badge>
                          </TableCell>
                          <TableCell>{sub.duree} mois</TableCell>
                          <TableCell className="font-mono text-sm">{sub.plaque}</TableCell>
                          <TableCell className="font-semibold">{sub.prix} €</TableCell>
                          <TableCell>
                            <Badge className={sub.status === "confirmed" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"} data-testid={`temp-status-${sub.id}`}>
                              {sub.status === "confirmed" ? "Payé" : sub.status === "payment_declared" ? "Paiement à vérifier" : "En attente"}
                            </Badge>
                            {sub.status === "payment_declared" && (
                              <Button size="sm" variant="ghost" className="ml-1 text-green-700" onClick={() => confirmTemporaire(sub.id)} data-testid={`temp-confirm-${sub.id}`}>
                                Valider
                              </Button>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {new Date(sub.created_at).toLocaleDateString("fr-FR")}
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!temporaireStats?.recent || temporaireStats.recent.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                            Aucune souscription temporaire pour le moment
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Clients Tab */}
          {activeTab === "clients" && (
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid md:grid-cols-4 gap-4">
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{clients.length}</p>
                        <p className="text-sm text-slate-500">Total clients</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                        <UserPlus className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">+12</p>
                        <p className="text-sm text-slate-500">Ce mois</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                        <Activity className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">85%</p>
                        <p className="text-sm text-slate-500">Taux actif</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                        <Award className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">4.8</p>
                        <p className="text-sm text-slate-500">Satisfaction</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Clients Table */}
              <Card className="border-0 shadow-sm">
                <CardHeader className="border-b">
                  <div className="flex items-center justify-between">
                    <CardTitle>Liste des clients</CardTitle>
                    <Button className="bg-primary hover:bg-primary/90">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Ajouter un client
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Client</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Contrats</TableHead>
                        <TableHead>Inscription</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clients.map((client) => (
                        <TableRow key={client.id} className="hover:bg-slate-50/50">
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                                <span className="font-medium text-slate-600">
                                  {client.first_name?.[0]}{client.last_name?.[0]}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium">{client.first_name} {client.last_name}</p>
                                <p className="text-sm text-slate-500">{client.city || 'Non renseigné'}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <p className="text-sm flex items-center gap-2">
                                <Mail className="w-3 h-3 text-slate-400" />
                                {client.email}
                              </p>
                              <p className="text-sm flex items-center gap-2">
                                <Phone className="w-3 h-3 text-slate-400" />
                                {client.phone || '-'}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{client.contracts_count || 0} contrat(s)</Badge>
                          </TableCell>
                          <TableCell className="text-slate-500">
                            {new Date(client.created_at).toLocaleDateString('fr-FR')}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm">
                              <Eye className="w-4 h-4 mr-2" />
                              Voir
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {clients.length === 0 && (
                    <div className="py-12 text-center text-slate-500">
                      <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Aucun client trouvé</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Commissions Tab */}
          {activeTab === "commissions" && (
            <div className="space-y-6">
              {/* Commission Stats */}
              <div className="grid md:grid-cols-4 gap-6">
                <Card className="border-0 shadow-sm" data-testid="total-commissions-card">
                  <CardContent className="p-6">
                    <p className="text-sm text-slate-500 mb-2">Total des commissions</p>
                    <p className="text-3xl font-bold text-slate-900">{(stats?.total_commissions || 0).toLocaleString()}€</p>
                    <div className="flex items-center gap-1 mt-2 text-emerald-600 text-sm">
                      <ArrowUpRight className="w-4 h-4" />
                      Commissions gagnées
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm" data-testid="pending-commissions-card">
                  <CardContent className="p-6">
                    <p className="text-sm text-slate-500 mb-2">En attente de versement</p>
                    <p className="text-3xl font-bold text-amber-600">{(stats?.pending_commissions || 0).toLocaleString()}€</p>
                    <p className="text-sm text-slate-500 mt-2">Contrats en attente de signature</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm" data-testid="frais-dossier-card">
                  <CardContent className="p-6">
                    <p className="text-sm text-slate-500 mb-2">Frais de dossier</p>
                    <p className="text-3xl font-bold text-slate-900">{(stats?.total_frais_dossier || 0).toLocaleString()}€</p>
                    <p className="text-sm text-slate-500 mt-2">{stats?.business_settings?.frais_dossier_min || 50}€ - {stats?.business_settings?.frais_dossier_max || 150}€ par dossier</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm bg-gradient-to-br from-primary to-primary/80 text-white">
                  <CardContent className="p-6">
                    <p className="text-sm text-white/70 mb-2">Taux de commission</p>
                    <p className="text-3xl font-bold">{stats?.business_settings?.commission_rate_default || 15}%</p>
                    <p className="text-sm text-white/70 mt-2">{stats?.business_settings?.commission_rate_min || 10}% - {stats?.business_settings?.commission_rate_max || 20}%</p>
                  </CardContent>
                </Card>
              </div>

              {/* Business Settings Card */}
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle>Paramètres de tarification</CardTitle>
                  <CardDescription>Frais de dossier et taux de commission configurés</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Frais de dossier */}
                    <div className="p-6 bg-slate-50 rounded-xl">
                      <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-primary" />
                        Frais de dossier
                      </h4>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-slate-600">Minimum</span>
                          <span className="font-semibold">{stats?.business_settings?.frais_dossier_min || 50}€</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-slate-600">Maximum</span>
                          <span className="font-semibold">{stats?.business_settings?.frais_dossier_max || 150}€</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t">
                          <span className="text-slate-600">Par défaut</span>
                          <span className="font-bold text-primary">{stats?.business_settings?.frais_dossier_default || 75}€</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Taux de commission */}
                    <div className="p-6 bg-slate-50 rounded-xl">
                      <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                        <Percent className="w-5 h-5 text-primary" />
                        Taux de commission
                      </h4>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-slate-600">Minimum</span>
                          <span className="font-semibold">{stats?.business_settings?.commission_rate_min || 10}%</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-slate-600">Maximum</span>
                          <span className="font-semibold">{stats?.business_settings?.commission_rate_max || 20}%</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t">
                          <span className="text-slate-600">Par défaut</span>
                          <span className="font-bold text-primary">{stats?.business_settings?.commission_rate_default || 15}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Commission by type */}
                  {financialData?.commissions_by_type && Object.keys(financialData.commissions_by_type).length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-semibold text-slate-800 mb-4">Commissions par type d'assurance</h4>
                      <div className="grid md:grid-cols-3 gap-4">
                        {Object.entries(financialData.commissions_by_type).map(([type, data]) => (
                          <div key={type} className="p-4 bg-white border rounded-lg">
                            <Badge variant="secondary" className="mb-2">{insuranceLabels[type] || type}</Badge>
                            <p className="text-xl font-bold text-slate-900">{data.amount.toLocaleString()}€</p>
                            <p className="text-sm text-slate-500">{data.count} contrat(s)</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Partners Tab */}
          {activeTab === "partners" && (
            <div className="space-y-6">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle>Espace Partenaires</CardTitle>
                  <CardDescription>Gérez vos partenaires et apporteurs d'affaires</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12 text-slate-500">
                    <Handshake className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>Module partenaires</p>
                    <p className="text-sm">Intégration à venir</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* AI & Leads Tab */}
          {activeTab === "ai-insights" && (
            <div className="space-y-6">
              {/* AI Stats Cards */}
              <div className="grid md:grid-cols-4 gap-6">
                <Card className="border-0 shadow-sm bg-gradient-to-br from-red-500 to-orange-500 text-white" data-testid="hot-leads-card">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <Flame className="w-5 h-5" />
                      <p className="text-sm text-white/80">Leads Chauds</p>
                    </div>
                    <p className="text-3xl font-bold">{aiDashboard?.leads?.hot_count || 0}</p>
                    <p className="text-sm text-white/70 mt-1">À contacter en priorité</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-400 to-yellow-500 text-white" data-testid="warm-leads-card">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <ThermometerSun className="w-5 h-5" />
                      <p className="text-sm text-white/80">Leads Tièdes</p>
                    </div>
                    <p className="text-3xl font-bold">{aiDashboard?.leads?.warm_count || 0}</p>
                    <p className="text-sm text-white/70 mt-1">Nurturing recommandé</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm" data-testid="chat-stats-card">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageSquare className="w-5 h-5 text-[#102692]" />
                      <p className="text-sm text-slate-500">Conversations IA</p>
                    </div>
                    <p className="text-3xl font-bold text-slate-900">{aiDashboard?.chat_stats?.total || 0}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-emerald-600">{aiDashboard?.chat_stats?.conversion_rate || 0}%</span>
                      <span className="text-xs text-slate-500">taux de conversion</span>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm" data-testid="visitor-stats-card">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-5 h-5 text-[#102692]" />
                      <p className="text-sm text-slate-500">Visiteurs Engagés</p>
                    </div>
                    <p className="text-3xl font-bold text-slate-900">{aiDashboard?.visitor_stats?.engaged || 0}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-emerald-600">{aiDashboard?.visitor_stats?.engagement_rate || 0}%</span>
                      <span className="text-xs text-slate-500">d'engagement</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Hot Leads Table */}
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Flame className="w-5 h-5 text-red-500" />
                        Leads Chauds - À contacter immédiatement
                      </CardTitle>
                      <CardDescription>Prospects avec le score le plus élevé, prêts à convertir</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchAllData}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Actualiser
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {aiDashboard?.leads?.hot?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Score</TableHead>
                          <TableHead>Visiteur</TableHead>
                          <TableHead>Intérêts</TableHead>
                          <TableHead>Actions recommandées</TableHead>
                          <TableHead>Valeur estimée</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {aiDashboard.leads.hot.map((lead, index) => (
                          <TableRow key={lead.visitor_id || index}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white font-bold">
                                  {lead.score}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <p className="font-medium text-slate-800">{lead.visitor_id?.substring(0, 12)}...</p>
                              <p className="text-xs text-slate-500">Lead #{index + 1}</p>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {lead.insurance_interest?.map((interest, i) => (
                                  <Badge key={i} variant="secondary" className="text-xs">
                                    {insuranceLabels[interest] || interest}
                                  </Badge>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell>
                              <ul className="text-xs text-slate-600 space-y-1">
                                {lead.recommended_actions?.slice(0, 2).map((action, i) => (
                                  <li key={i} className="flex items-center gap-1">
                                    <Zap className="w-3 h-3 text-amber-500" />
                                    {action}
                                  </li>
                                ))}
                              </ul>
                            </TableCell>
                            <TableCell>
                              <p className="font-semibold text-emerald-600">{lead.estimated_value?.toLocaleString() || 0}€</p>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-12 text-slate-500">
                      <Bot className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Aucun lead chaud pour le moment</p>
                      <p className="text-sm">Les leads seront qualifiés automatiquement par l'IA</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Warm Leads Table */}
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ThermometerSun className="w-5 h-5 text-amber-500" />
                    Leads Tièdes - Nurturing recommandé
                  </CardTitle>
                  <CardDescription>Prospects intéressés mais qui nécessitent plus de nurturing</CardDescription>
                </CardHeader>
                <CardContent>
                  {aiDashboard?.leads?.warm?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Score</TableHead>
                          <TableHead>Visiteur</TableHead>
                          <TableHead>Intérêts</TableHead>
                          <TableHead>Raisons de qualification</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {aiDashboard.leads.warm.slice(0, 5).map((lead, index) => (
                          <TableRow key={lead.visitor_id || index}>
                            <TableCell>
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center text-white font-bold text-sm">
                                {lead.score}
                              </div>
                            </TableCell>
                            <TableCell>
                              <p className="font-medium text-slate-800">{lead.visitor_id?.substring(0, 12)}...</p>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {lead.insurance_interest?.map((interest, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">
                                    {insuranceLabels[interest] || interest}
                                  </Badge>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell>
                              <ul className="text-xs text-slate-500">
                                {lead.qualification_reasons?.slice(0, 2).map((reason, i) => (
                                  <li key={i}>• {reason}</li>
                                ))}
                              </ul>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Snowflake className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm">Aucun lead tiède pour le moment</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <ChatSummaries />

              {/* AI System Info */}
              <Card className="border-0 shadow-sm bg-gradient-to-br from-slate-50 to-slate-100">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BrainCircuit className="w-5 h-5 text-[#102692]" />
                    Système d'Intelligence Artificielle JYVIS
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="p-4 bg-white rounded-xl shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <Bot className="w-5 h-5 text-[#102692]" />
                        <h4 className="font-semibold">Sophie - Chatbot IA</h4>
                      </div>
                      <p className="text-sm text-slate-600">Conseillère virtuelle disponible 24/7 pour qualifier les leads et répondre aux questions.</p>
                      <Badge className="mt-2 bg-emerald-100 text-emerald-700">Actif</Badge>
                    </div>
                    <div className="p-4 bg-white rounded-xl shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="w-5 h-5 text-[#102692]" />
                        <h4 className="font-semibold">Scoring Automatique</h4>
                      </div>
                      <p className="text-sm text-slate-600">Analyse comportementale en temps réel pour identifier les meilleurs prospects.</p>
                      <Badge className="mt-2 bg-emerald-100 text-emerald-700">Actif</Badge>
                    </div>
                    <div className="p-4 bg-white rounded-xl shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-5 h-5 text-[#102692]" />
                        <h4 className="font-semibold">Recommandations IA</h4>
                      </div>
                      <p className="text-sm text-slate-600">Suggestions personnalisées de produits et offres pour chaque visiteur.</p>
                      <Badge className="mt-2 bg-emerald-100 text-emerald-700">Actif</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === "calc-leads" && <CalcLeads />}
          {activeTab === "pricing" && <PricingEditor />}
          {activeTab === "reviews" && <ReviewReplies />}

          {/* Support Tab */}
          {activeTab === "support" && (
            <div className="space-y-6">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle>Centre de support</CardTitle>
                  <CardDescription>Gérez les demandes de support client</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12 text-slate-500">
                    <HeadphonesIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>Module support</p>
                    <p className="text-sm">Intégration Intercom à venir</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
